import type { ComponentProps } from 'react';

import { clsxm } from '@vs-shop/utils';
import { cva } from 'class-variance-authority';

export const textareaVariants = cva(
  [
    // Basic layout
    'relative block h-full w-full appearance-none rounded-sm px-5 py-4',
    // Typography
    'text-foreground-muted placeholder:text-foreground-muted text-base/6 tracking-tight',
    // Border drawn as a ring
    'ring-foreground/40 hover:ring-foreground/60 ring',
    // Background (the wrapper's ::before paints the surface)
    'bg-transparent',
    // The visible focus ring is drawn by the wrapper's ::after, so suppress the native one
    'focus:outline-hidden',
  ],
  {
    variants: {
      state: {
        base: '',
        error: 'bg-destructive/5 ring-destructive hover:ring-destructive hover:ring-2',
      },
      resize: {
        y: 'resize-y',
        none: 'resize-none',
      },
    },
    defaultVariants: { state: 'base', resize: 'y' },
  }
);

export type TextareaProps = Omit<ComponentProps<'textarea'>, 'children'> & {
  resizable?: boolean;
  error?: string | null;
};

export const Textarea = ({
  id,
  className,
  rows = 4,
  resizable = true,
  error,
  ...props
}: TextareaProps) => {
  const errorId = id ? `${id}-error` : undefined;

  return (
    <div data-slot="textarea" className="w-full">
      <span
        data-slot="textarea-box"
        className={clsxm(
          // Basic layout
          'relative block w-full rounded-sm',
          // Surface painted on the inset ::before so the shadow blends with the border in light mode
          'before:bg-background before:absolute before:inset-px',
          // In dark mode the surface moves to the control and the shadow is dropped, so hide ::before
          'dark:before:hidden',
          // Focus ring
          'after:focus-within:ring-foreground/60 after:pointer-events-none after:absolute after:inset-0 after:rounded-sm after:ring-inset after:focus-within:ring-1',
          // Disabled state (native :disabled on the descendant control)
          'before:has-[:disabled]:bg-background-intense has-[:disabled]:opacity-50 before:has-[:disabled]:shadow-none',
          className
        )}
      >
        <textarea
          id={id}
          rows={rows}
          data-slot="textarea-control"
          aria-invalid={!!error}
          aria-describedby={error ? errorId : undefined}
          data-invalid={error ? '' : undefined}
          className={clsxm(
            textareaVariants({ state: error ? 'error' : 'base', resize: resizable ? 'y' : 'none' })
          )}
          {...props}
        />
      </span>

      {error && (
        <div id={errorId} data-slot="textarea-error" className="text-destructive mt-1 px-2 text-xs">
          {error}
        </div>
      )}
    </div>
  );
};
